import type { OrganizationDetail, SessionRecord } from '@viewer/protocol';
import { useApi } from '../../lib/api/useApi';
import { accessTypeLabel, authMethodLabel, endReasonLabel, formatDateTime, sessionStatusLabel } from './labels';
import styles from './org.module.css';

interface Props {
  org: OrganizationDetail;
}

function duration(s: SessionRecord): string | null {
  if (!s.acceptedAt) return null;
  const end = s.endedAt ? new Date(s.endedAt).getTime() : Date.now();
  const minutes = Math.max(1, Math.round((end - new Date(s.acceptedAt).getTime()) / 60_000));
  return minutes < 60 ? `${minutes} min` : `${Math.floor(minutes / 60)} h ${minutes % 60} min`;
}

export function SessionsTab({ org }: Props) {
  const sessions = useApi<SessionRecord[]>(`/api/orgs/${org.id}/sessions?limit=100`, 15_000);

  return (
    <section className={styles.panel}>
      <div className={styles.panelHead}>
        <h2 className={styles.panelTitle}>Sessões</h2>
      </div>
      <p className={styles.help}>
        Quem acessou qual computador, quando, com que tipo de acesso e como entrou. Acesso externo é de quem entrou sem
        permissão da empresa, por aceite ou senha no próprio computador.
      </p>
      {sessions.error && <p className={styles.error}>{sessions.error}</p>}
      {sessions.data?.length === 0 && <p className={styles.empty}>Nenhuma sessão registrada nesta empresa.</p>}
      <div className={styles.list}>
        {sessions.data?.map((s) => (
          <div key={s.id} className={styles.row}>
            <div className={styles.rowMain}>
              <span className={styles.rowName}>
                {s.userName ?? s.viewerDeviceName}
                {' → '}
                {s.hostDeviceName}
                {s.status === 'active' && <span className={`${styles.badge} ${styles.badgeAccent}`}>ao vivo</span>}
                {s.external && (
                  <span className={styles.badge} title="Entrou sem permissão da empresa, por aceite ou senha no próprio computador">
                    acesso externo
                  </span>
                )}
              </span>
              <span className={styles.rowMeta}>
                {formatDateTime(s.acceptedAt ?? s.createdAt)}
                {duration(s) && ` · ${duration(s)}`}
                {s.accessType && ` · ${accessTypeLabel[s.accessType]}`}
                {s.authMethod && ` · ${authMethodLabel[s.authMethod]}`}
                {s.userEmail && ` · ${s.userEmail}`}
              </span>
            </div>
            <span className={styles.badge}>
              {sessionStatusLabel[s.status]}
              {s.endReason && s.status === 'ended' && ` · ${endReasonLabel[s.endReason]}`}
            </span>
          </div>
        ))}
      </div>
    </section>
  );
}
